import create from "zustand";
import { ILoginValues, IUser } from "api/User/User.types";
import UserApi from "api/User/User.api";
import Cookies from "js-cookie";
import { fetchUserDetails, getUserFromAccessToken } from "utils/auth";
import useRbacStore from "./Rbac.store";

const TOKEN_KEY = process.env.REACT_APP_AUTH_COOKIE_NAME || "access_token";

const useUserStore = create<AppStoreState>((set, get) => ({
  user: undefined,
  loadingUser: false,
  userExistsChecked: false,

  login: async (values: ILoginValues) => {
    set({ loadingUser: true });
    let user = await UserApi.login(values);
    let toSet: any = { loadingUser: false };
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      toSet["user"] = user;
    }
    set(toSet);
    if (user) {
      await useRbacStore.getState().fetchContext();
    }
    return user;
  },

  logout: () => {
    Cookies.remove(TOKEN_KEY);
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
    localStorage.removeItem("user");
    set({ user: undefined, userExistsChecked: false });

    const authServiceUrl = process.env.REACT_APP_AUTH_SERVICE_URL;
    if (authServiceUrl) {
      const redirectUrl = process.env.REACT_APP_APP_URL || window.location.origin;
      window.location.href = `${authServiceUrl}?redirect_url=${encodeURIComponent(redirectUrl)}`;
    }
  },

  checkUserExists: async () => {
    let userJson = localStorage.getItem("user");
    let user: IUser | undefined;
    if (userJson) {
      try {
        user = JSON.parse(userJson) as IUser;
      } catch {
        user = undefined;
      }
    }
    if (!user) {
      const accessToken =
        Cookies.get(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
      if (accessToken) {
        const fromApi = await fetchUserDetails(accessToken);
        if (fromApi) {
          try {
            localStorage.setItem("user", JSON.stringify(fromApi));
          } catch {
            // ignore
          }
          user = fromApi;
        }
        if (!user) {
          const hydrated = getUserFromAccessToken(accessToken);
          if (hydrated) {
            try {
              localStorage.setItem("user", JSON.stringify(hydrated));
            } catch {
              // ignore
            }
            user = hydrated;
          }
        }
      }
    }
    set({ userExistsChecked: true, user });
    if (user) {
      await useRbacStore.getState().fetchContext();
    }
  },

  setLoadingUser: (loadingUser: boolean) => set({ loadingUser }),
  setUser: (user: IUser) => set({ user }),
}));

interface AppStoreState {
  user?: IUser;
  loadingUser: boolean;
  login: (values: ILoginValues) => any;
  logout: () => void;
  checkUserExists: () => void | Promise<void>;
  setLoadingUser: (loadingUser: boolean) => void;
  setUser: (user: IUser) => void;
  userExistsChecked: boolean;
}

export default useUserStore;
